import { z } from 'zod';
import type { CopyComparison } from '../greek/diff';
import type { EvaluationResult, Rubric, TranscriptionResult, UncertainSpan } from './ai';
import type { PageTemplate } from './ink';
import type { Register, SourceType, Topic } from './content';

/* ───────────────────────────── Errors ───────────────────────────── */

export const ERROR_CODES = [
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CSRF_REJECTED',
  'RATE_LIMITED',
  'INVALID_CODE',
  'CODE_EXPIRED',
  'TOO_MANY_ATTEMPTS',
  'REVISION_CONFLICT',
  'SESSION_LOCKED',
  'EXAM_TIME_EXPIRED',
  'EXAM_NOT_ACTIVE',
  'NOT_SYNCED',
  'TRANSCRIPT_NOT_CONFIRMED',
  'TRANSCRIPT_STALE',
  'REFERENCE_HIDDEN',
  'AI_UNAVAILABLE',
  'AI_BUDGET_EXCEEDED',
  'AI_INVALID_OUTPUT',
  'PAYLOAD_TOO_LARGE',
  'STORAGE_ERROR',
  'TEACHER_NOT_FOUND',
  'CONFLICT',
  'INTERNAL',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; details?: unknown; requestId?: string };
}

/** Header every mutating request must carry (CSRF defence, see ADR-0003). */
export const CSRF_HEADER = 'x-requested-with';
export const CSRF_HEADER_VALUE = 'greek-notebook';

/* ───────────────────────────── Auth ───────────────────────────── */

export const RequestCodeSchema = z.object({ email: z.string().trim().min(3).max(254) });
export const VerifyCodeSchema = z.object({
  email: z.string().trim().min(3).max(254),
  code: z.string().trim().regex(/^\d{6}$/),
});

export const ROLES = ['student', 'teacher', 'admin'] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

export interface MeResponse {
  user: { id: string; email: string; displayName: string | null; roles: Role[]; locale: 'ru' | 'en' };
  session: { expiresAt: string; createdAt: string };
  features: { teacher: boolean; ai: boolean; aiProvider: string };
}

export const UpdateProfileSchema = z.object({
  displayName: z.string().trim().max(80).nullable().optional(),
  locale: z.enum(['ru', 'en']).optional(),
});

/* ───────────────────────────── Tasks ───────────────────────────── */

export const MODES = ['copy', 'recall', 'free', 'exam', 'drill'] as const;
export const ModeSchema = z.enum(MODES);
export type Mode = z.infer<typeof ModeSchema>;

export const TaskQuerySchema = z.object({
  mode: z.enum(['copy', 'recall', 'free', 'exam']).optional(),
  level: z.string().optional(),
  register: z.enum(['informal', 'formal', 'semi_formal']).optional(),
  topic: z.string().optional(),
  source: z.string().optional(),
  q: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(60),
  cursor: z.string().optional(),
});

export interface SourceDTO {
  id: string;
  key: string;
  type: SourceType;
  title: string;
  url: string | null;
  year: number | null;
  license: string;
  verifiedAt: string | null;
}

export interface TaskSummary {
  id: string;
  key: string;
  level: string;
  register: Register;
  letterType: string;
  topic: Topic;
  titleEl: string;
  titleRu: string;
  modes: string[];
  difficulty: number;
  tags: string[];
  wordLimits: { min: number; max: number };
  hasReference: boolean;
  referenceWordCount: number | null;
  reviewStatus: string;
  promptProvenance: string;
  source: Pick<SourceDTO, 'key' | 'type' | 'title'>;
  attempts: number;
  bestAccuracy: number | null;
}

export interface ReferenceDTO {
  id: string;
  version: number;
  textEl: string;
  translationRu: string | null;
  checksum: string;
  reviewStatus: string;
  authoredBy: string;
}

export interface TaskDetail extends TaskSummary {
  promptEl: string;
  promptRu: string;
  requiredPoints: { el: string; ru: string }[];
  learningGoals: string[];
  vocabulary: { el: string; ru: string }[];
  copyrightNote: string;
  sourceFull: SourceDTO;
  /** Absent when hidden by a strict recall session. */
  reference: ReferenceDTO | null;
}

/* ───────────────────────────── Writing sessions ───────────────────────────── */

export const SESSION_STATES = [
  'draft',
  'submitted',
  'transcribing',
  'transcription_failed',
  'awaiting_confirmation',
  'evaluating',
  'evaluation_failed',
  'evaluated',
] as const;
export type SessionState = (typeof SESSION_STATES)[number];

export const CreateSessionSchema = z.object({
  /** Client-generated id so drafts can be created offline and synced idempotently. */
  id: z.uuid(),
  mode: ModeSchema,
  taskId: z.uuid().nullable().optional(),
  /** Optional free-writing topic typed by the learner (stored as untrusted text). */
  customPromptEl: z.string().max(2000).nullable().optional(),
  /** Drill: launched from a feedback issue. */
  drillOf: z.object({ sessionId: z.uuid(), issueId: z.string().max(80), text: z.string().min(1).max(1000) }).optional(),
  recall: z.object({ strict: z.boolean(), studySeconds: z.number().int().min(0).max(3600) }).optional(),
  template: z.enum(['lined', 'grid', 'blank', 'exam']).default('lined'),
});
export type CreateSessionRequest = z.infer<typeof CreateSessionSchema>;

export interface PageMeta {
  id: string;
  order: number;
  revision: number;
  sha256: string | null;
  strokeCount: number;
  template: PageTemplate;
  updatedAt: string;
}

export interface SessionSummary {
  id: string;
  mode: Mode;
  state: SessionState;
  title: string;
  taskId: string | null;
  examId: string | null;
  startedAt: string;
  submittedAt: string | null;
  updatedAt: string;
  durationSeconds: number | null;
  pageCount: number;
  headline: { kind: 'accuracy' | 'score'; value: number; max: number } | null;
  sharedWith: number;
}

export interface SessionDetail extends SessionSummary {
  task: TaskDetail | null;
  customPromptEl: string | null;
  drillText: string | null;
  recall: { strict: boolean; studySeconds: number; hiddenAt: string | null; hintsUsed: string[] } | null;
  pages: PageMeta[];
  submittedRevisions: Record<string, number> | null;
  latestTranscription: TranscriptionDTO | null;
  latestEvaluation: EvaluationDTO | null;
  activeJob: JobDTO | null;
  /** True when the viewer is not the owner (teacher view). */
  readOnly: boolean;
}

export const SubmitSessionSchema = z.object({
  idempotencyKey: z.string().min(8).max(100),
  /** pageId → revision the client believes is final; server rejects with NOT_SYNCED when behind. */
  pageRevisions: z.record(z.string(), z.number().int().min(0)),
  durationSeconds: z.number().int().min(0).max(86400).optional(),
});

export const SessionsQuerySchema = z.object({
  mode: ModeSchema.optional(),
  state: z.string().optional(),
  q: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  cursor: z.string().optional(),
});

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

/* ───────────────────────────── Transcription ───────────────────────────── */

export interface TranscriptionDTO {
  id: string;
  version: number;
  rawText: string | null;
  confirmedText: string | null;
  confirmedAt: string | null;
  uncertainSpans: UncertainSpan[];
  notes: string[];
  pages: TranscriptionResult['pages'];
  provider: string;
  model: string | null;
  basedOnVersion: number | null;
  createdAt: string;
}

export const ConfirmTranscriptionSchema = z.object({
  basedOnVersion: z.number().int().min(0),
  confirmedText: z.string().max(20000),
});

/* ───────────────────────────── Evaluation ───────────────────────────── */

export const EvaluateSchema = z.object({
  idempotencyKey: z.string().min(8).max(100),
  provider: z.enum(['anthropic', 'openai', 'mock']).optional(),
  model: z.string().max(100).optional(),
});

export type EvaluationKind = 'copy' | 'free';

export interface EvaluationDTO {
  id: string;
  version: number;
  kind: EvaluationKind;
  transcriptionId: string;
  transcriptionVersion: number;
  status: 'completed' | 'failed';
  provider: string;
  model: string | null;
  promptVersion: string;
  rubricVersion: string | null;
  createdAt: string;
  costUsd: number | null;
  copy: CopyComparison | null;
  free: EvaluationResult | null;
  error: { code: string; message: string } | null;
}

export interface FeedbackResponse {
  session: SessionSummary;
  transcription: TranscriptionDTO | null;
  evaluations: EvaluationDTO[];
  reviews: TeacherReviewDTO[];
}

/* ───────────────────────────── Jobs ───────────────────────────── */

export interface JobDTO {
  id: string;
  type: 'transcribe' | 'evaluate';
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  sessionId: string;
  retries: number;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/* ───────────────────────────── Exams ───────────────────────────── */

export interface ExamProfileDTO {
  id: string;
  key: string;
  board: string;
  level: string;
  label: string;
  taskCount: number;
  timeLimitMinutes: number;
  wordCountRules: { min: number; max: number }[];
  rubric: Rubric;
  register: string[];
  sourceUrl: string | null;
  verifiedAt: string | null;
  official: boolean;
}

export const CreateExamSchema = z.object({
  id: z.uuid(),
  profileId: z.uuid(),
  timed: z.boolean().default(true),
  taskIds: z.array(z.uuid()).max(4).optional(),
});

export interface ExamDTO {
  id: string;
  profile: ExamProfileDTO;
  status: 'active' | 'submitted' | 'expired';
  timed: boolean;
  startAt: string;
  deadlineAt: string | null;
  submittedAt: string | null;
  serverNow: string;
  sessions: { sessionId: string; taskId: string; order: number; state: SessionState }[];
  tasks: TaskDetail[];
  report: ExamReport | null;
}

export interface ExamReport {
  label: string;
  perTask: { sessionId: string; taskTitle: string; score: number | null; max: number; wordCount: number | null }[];
  total: { score: number; max: number } | null;
  complete: boolean;
}

export const SubmitExamSchema = z.object({
  idempotencyKey: z.string().min(8).max(100),
  pageRevisions: z.record(z.string(), z.record(z.string(), z.number().int().min(0))),
  reason: z.enum(['manual', 'timeout']).default('manual'),
});

/* ───────────────────────────── Sharing / teacher ───────────────────────────── */

export const ShareSchema = z.object({
  teacherEmail: z.string().trim().min(3).max(254),
  allowComments: z.boolean().default(true),
});

export interface ShareDTO {
  id: string;
  sessionId: string;
  teacher: { id: string; email: string; displayName: string | null };
  owner: { id: string; email: string; displayName: string | null };
  permissions: { comment: boolean; score: boolean };
  createdAt: string;
  revokedAt: string | null;
  sessionTitle?: string;
  sessionMode?: Mode;
}

export const TeacherReviewSchema = z.object({
  scores: z
    .array(z.object({ criterion: z.string().min(1).max(60), score: z.number().min(0), max: z.number().positive() }))
    .max(10)
    .default([]),
  comments: z
    .array(
      z.object({
        text: z.string().min(1).max(4000),
        /** Optional anchor in the confirmed transcript. */
        start: z.number().int().min(0).nullable().default(null),
        end: z.number().int().min(0).nullable().default(null),
      }),
    )
    .max(100)
    .default([]),
  summary: z.string().max(4000).default(''),
});

export interface TeacherReviewDTO {
  id: string;
  shareId: string;
  teacher: { id: string; email: string; displayName: string | null };
  scores: { criterion: string; score: number; max: number }[];
  comments: { text: string; start: number | null; end: number | null }[];
  summary: string;
  transcriptionVersion: number | null;
  createdAt: string;
}

/* ───────────────────────────── Progress ───────────────────────────── */

export interface ProgressResponse {
  totals: {
    sessions: number;
    submitted: number;
    words: number;
    minutes: number;
    byMode: Record<string, number>;
  };
  copyTrend: { date: string; sessionId: string; charAccuracy: number; accentAccuracy: number; punctuationAccuracy: number }[];
  freeTrend: { date: string; sessionId: string; score: number; max: number }[];
  categories: { category: string; count: number }[];
  repeatedErrors: { word: string; type: string; occurrences: number; lastSeen: string; sessions: number }[];
  perTask: { taskId: string; title: string; attempts: number; best: number | null; last: number | null }[];
  streakDays: number;
}

/* ───────────────────────────── Admin ───────────────────────────── */

export const AllowlistCreateSchema = z.object({
  email: z.string().trim().min(3).max(254),
  role: RoleSchema,
  sendInvite: z.boolean().default(false),
});

export const AllowlistUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  roles: z.array(RoleSchema).min(1).optional(),
});

export interface AllowlistEntryDTO {
  id: string;
  email: string;
  role: Role;
  enabled: boolean;
  createdAt: string;
  addedBy: string | null;
  user: { id: string; roles: Role[]; lastLoginAt: string | null } | null;
}

export const AdminSettingsSchema = z.object({
  aiProvider: z.enum(['anthropic', 'openai', 'mock']).optional(),
  anthropicVisionModel: z.string().max(100).optional(),
  anthropicEvaluationModel: z.string().max(100).optional(),
  openaiVisionModel: z.string().max(100).optional(),
  openaiEvaluationModel: z.string().max(100).optional(),
  monthlyBudgetUsd: z.number().min(0).max(10000).optional(),
  teacherFeature: z.boolean().optional(),
});

export interface AdminSettingsDTO {
  aiProvider: string;
  anthropicVisionModel: string | null;
  anthropicEvaluationModel: string | null;
  openaiVisionModel: string | null;
  openaiEvaluationModel: string | null;
  monthlyBudgetUsd: number;
  teacherFeature: boolean;
  providersConfigured: { anthropic: boolean; openai: boolean; mock: boolean };
  pricingConfigured: string[];
}

export interface UsageDTO {
  monthToDate: { costUsd: number; inputTokens: number; outputTokens: number; jobs: number; failed: number };
  budgetUsd: number;
  recent: {
    id: string;
    type: string;
    status: string;
    provider: string | null;
    model: string | null;
    costUsd: number | null;
    inputTokens: number | null;
    outputTokens: number | null;
    latencyMs: number | null;
    createdAt: string;
    errorCode: string | null;
  }[];
  storage: { bytes: number; files: number };
  db: { sizeBytes: number | null };
  backup: { lastStatus: string | null; lastAt: string | null };
}

export interface AuditEventDTO {
  id: string;
  actor: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}
